// Where a fax tells you to reply.
//
// Printed into the page, because that is the only carrier that survives the
// second hop: a screenshot of the fax, a re-share, a photo of a printout.
// Messengers strip EXIF and recompress, so metadata is not an option — and a
// printed sender address is period-accurate anyway.
//
// TWO FORMS OF THE SAME ADDRESS, and the difference matters.
//
//   the PRINTED one   bare, short, typeable, and it has to survive being
//                     dithered to 1-bit and then recompressed by WhatsApp
//   the SHARED one    a real tappable link in the message body, so it can
//                     afford to carry who the fax was from
//
// The second is the loop. Somebody opens `/f?re=MUM'S GOOD MACHINE`, the
// landing page hands the parameter to `/app`, and the app opens on a cover
// sheet already addressed back — see `defineLinks()` in App.vue, which is what
// reads it. Without that declaration nothing on this side is wired to anything:
// ComposePage has always read `route.query.re`, but a memory-history router
// never sees a URL, so the parameter arrived nowhere and the reply loop was one
// unreachable branch at each end.

import { PUBLIC_ORIGIN } from '../../app-config.js';

const origin = (PUBLIC_ORIGIN || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

/** Short enough to read after a messenger has recompressed the image. */
export const REPLY_URL = origin || 'justthefax.jeremymarks.com.au';

/**
 * The same address, carrying who to reply to.
 *
 * SANITISED HERE AS WELL AS ON THE WAY IN. A machine name is user input, it
 * ends up in a URL that a third party opens, and ComposePage prints whatever
 * comes back onto an image somebody else will see. Both ends narrow it to the
 * characters a fax header can print.
 */
export function replyLink(fromName = '') {
  const re = String(fromName || '')
    .toUpperCase().replace(/[^A-Z0-9 .&'-]/g, '').trim().slice(0, 20);
  const url = `https://${REPLY_URL}/f`;
  return re ? `${url}?re=${encodeURIComponent(re)}` : url;
}

/** What rides in the share sheet's text, beside the image. */
export const shareText = (to, from = '') =>
  `Incoming fax${to ? ` for ${to}` : ''}. Reply with your own: ${replyLink(from)}`;

/**
 * The footer line printed on every page: the reply link WITH the sender's
 * name, minus the scheme. Slack and its kind take the URL out of a share's
 * caption and post that instead of the picture, so the picture itself has
 * to carry the whole loop; and a printed link is the only channel that
 * survives a screenshot, a re-share, or a photo of a printout anyway.
 */
export const replyFooter = (fromName = '') => replyLink(fromName).replace(/^https?:\/\//, '');
